import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { stringify } from 'qs';
import config from 'walden-fast/config/environment';
import type SessionService from 'walden-fast/services/session';
import type ImpersonateService from 'walden-fast/services/impersonate';
import type CacheManagerService from './cache-manager';
import type { CacheOptions } from './cache-manager';
import type { ValidationProblemDetails } from 'walden-fast/types/fast-backend/http';

export default class FastApiService extends Service {
  @service('session') declare sessionService: SessionService;
  @service('router') declare routerService: RouterService;
  @service('impersonate') declare impersonateService: ImpersonateService;
  @service('cache-manager') declare cacheManager: CacheManagerService;

  get authHeaders() {
    const headers: Record<string, string> = {};

    const { accessToken } = this.sessionService;
    if (accessToken) {
      headers['authorization'] = `Bearer ${accessToken}`;
    }

    const impersonatingId = this.impersonateService.getImpersonatingId();
    if (impersonatingId) {
      headers['Impersonate-Account'] = impersonatingId;
    }

    return Object.keys(headers).length > 0 ? headers : undefined;
  }

  private createHttp401Error(): Error & { status: 401 } {
    type Http401Error = Error & {
      status: 401;
    };
    return Object.assign(new Error('HTTP 401 Unauthorized'), {
      status: 401 as const,
    }) as Http401Error;
  }

  async get<ReturnType>(
    url: string,
    queryParams?: Record<string, string | number>,
    cache?: {
      key: string;
      options?: CacheOptions;
    },
  ): Promise<ReturnType | undefined> {
    // Try to get from cache first
    if (cache) {
      const cached = this.cacheManager.getCache<ReturnType>(
        cache.key,
        cache.options,
      );
      if (cached) return cached;
    }

    if (!this.sessionService.accessTokenValid) {
      await this.sessionService.fetchAccessToken();
    }

    const query = queryParams ? `?${stringify(queryParams)}` : '';
    return fetch(`${config.fastApiURL}` + url + query, {
      headers: {
        Accept: 'application/json',
        ...this.authHeaders,
      },
    })
      .then(async (response) => {
        if (response.status === 401) {
          await this.sessionService.redirectToLoginIfNotAuthenticated();
          throw this.createHttp401Error();
        }

        if (response.status === 403) {
          console.warn('user is not allowed to fetch', url);
          this.routerService.transitionTo('unauthorized');
          return;
        }

        if (response.status === 404) {
          return;
        }

        if (response.status === 500) {
          return response.json().then((body: { message: string }) => {
            throw new Error(body.message);
          });
        }

        const responseData = (await response.json()) as ReturnType;

        // Cache the response
        if (cache) {
          this.cacheManager.setCache<ReturnType>(
            cache.key,
            responseData,
            cache.options,
          );
        }

        return responseData;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  async post<ReturnType, BodyType>(
    url: string,
    body: BodyType,
  ): Promise<ReturnType | undefined> {
    if (!this.sessionService.accessTokenValid) {
      await this.sessionService.fetchAccessToken();
    }

    return fetch(`${config.fastApiURL}` + url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...this.authHeaders,
      },
    })
      .then(async (response) => {
        if (response.status === 401) {
          await this.sessionService.redirectToLoginIfNotAuthenticated();
          throw this.createHttp401Error();
        }

        if (response.status === 403) {
          console.warn('user is not allowed to post to', url);
          this.routerService.transitionTo('unauthorized');
          return;
        }

        if (response.status === 400) {
          return response.json().then((payload: ValidationProblemDetails) => {
            type Http400Error = Error & {
              status: 400;
              payload: ValidationProblemDetails;
            };
            const err = Object.assign(new Error('HTTP 400'), {
              status: 400 as const,
              payload,
            }) as Http400Error;

            throw err;
          });
        }
        if (
          response.status === 500 &&
          response.headers.get('Content-Type') === 'application/json'
        ) {
          return response.json().then((body: { message: string }) => {
            throw new Error(body.message);
          });
        }

        if (response.status === 500) {
          return response.text().then((body: string) => {
            throw new Error(body);
          });
        }

        return response.json() as ReturnType;
      })
      .catch((error) => {
        throw error;
      });
  }

  postExternal<ReturnType, BodyType>(
    url: string,
    body: BodyType,
    attrs = {},
  ): Promise<ReturnType> {
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      ...attrs,
    }).then((response) => {
      if (response.status != 200) {
        throw new Error(`Api call failed with status ${response.status}`);
      }
      return response.json();
    });
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:fast-api')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('fast-api') declare altName: FastApiService;`.
declare module '@ember/service' {
  interface Registry {
    'fast-api': FastApiService;
  }
}
